import React from 'react';
import styled from '@emotion/styled';
import { limitFit } from '@cloudinary/url-gen/actions/resize';
import { breakpoints } from '@naf/theme';
import { TextWithImage } from '@naf-simba/sections-text-with-image';
import { TextAndImageBlockType } from '@appTypes/TextAndImageBlockType';
import { useCloudinary } from '@hooks/Cloudinary/useCloudinary';
import BlockContent from '@components/block-content/BlockContent';
import { groupAccordionRuns } from './TextAndImageBlock.utils';

const IMAGE_WIDTHS = [400, 800, 1200, 1600, 2000];

export const TextAndImageBlock = ({ block }: { block: TextAndImageBlockType }) => {
  const { cld } = useCloudinary();
  const publicId = block?.image?.publicId;

  const imageUrl = (width: number) =>
    cld?.image(publicId).resize(limitFit().width(width)).quality('auto:best').format('auto').toURL();

  const image =
    cld && publicId ? (
      <img
        loading="lazy"
        sizes={`(min-width: ${breakpoints.s}) ${block.imageAlignment?.relativeSize ?? 50}vw, 100vw`}
        srcSet={IMAGE_WIDTHS.map((width) => `${imageUrl(width)} ${width}w`).join(', ')}
        src={imageUrl(800)}
        alt={block.image.alt || block.image.altText}
        style={{ width: '100%' }}
      />
    ) : undefined;

  return (
    <StyledTextWithImage
      contentAlignment={block.textContentAlignment === 'center' ? 'center' : 'start'}
      title={block.title}
      className="TextAndImageBlock"
      media={image ? { render: () => image } : undefined}
      imagePosition={block.imageAlignment?.horizontal === 'left' ? 'start' : 'end'}
      mobileImagePosition={block.imageAlignment?.vertical === 'above' ? 'start' : 'end'}
      imageWidth={block.imageAlignment?.relativeSize}
    >
      {groupAccordionRuns(block.text).map((run) =>
        run.isAccordions ? (
          <div key={run.key}>
            <BlockContent value={run.blocks} />
          </div>
        ) : (
          <BlockContent key={run.key} value={run.blocks} />
        ),
      )}
    </StyledTextWithImage>
  );
};

// TextWithImage stacks its content in a flex column, which stretches a standalone link to the full
// width, and the simba Link centres its content — so keep it at its own width, on the left.
const StyledTextWithImage = styled(TextWithImage)`
  a {
    align-self: flex-start;
  }
`;
